import React, { useState } from "react";
import DetailsCard from "../../DetailsCard";
import { AudienceModel } from "../../../redux/campaignTypes";
import { createUseStyles } from "react-jss";
import { Colors } from "../../../constants/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faEye, faUsersViewfinder } from "@fortawesome/free-solid-svg-icons";
import CampaignRecipientsModal from "../CampaignRecipientsModal";

type Props = {
  audience?: AudienceModel;
  canEdit: boolean;
  onEdit?: () => void;
};

const AudienceCard = ({ audience, canEdit, onEdit }: Props) => {
  const styles = useStyles();
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);

  const count = audience?.recipients?.length ?? 0;
  const onClick = canEdit && onEdit ? onEdit : undefined;

  const renderAudienceInfo = () => {
    if (!audience) {
      return (
        <p>
          <span>No audience defined.</span>
        </p>
      );
    }

    const renderSelectedUsers = () => (
      <div className={styles.usersSection}>
        <div className={styles.usersCount}>
          <FontAwesomeIcon icon={faUsersViewfinder} className={styles.usersIcon} />
          <span className={styles.countText}>Resolved recipients: </span>
          <div
            className={styles.countBadge}
            onClick={(e) => {
              e.stopPropagation();
              setShowRecipientsModal(true);
            }}
          >
            <span className={styles.countNumber}>{count.toLocaleString()}</span>
            <FontAwesomeIcon icon={faEye} className={styles.viewIcon} />
          </div>
        </div>
      </div>
    );

    switch (audience.type) {
      case 'individuals':
        return (
          <>
            <p>
              <span>Selected users.</span> Will send to all the specified users, no matter their status, as long
              as they are present in the system. If any specified user is not present during campaign execution, the
              campaign will fail.
            </p>
            {renderSelectedUsers()}
          </>
        );

      case 'activeEmployees': {
        const employers = audience.employers ?? [];
        const hasEmployers = employers.length > 0;

        return (
          <>
            <p>
              <span>All active employees.</span> Recipients are determined during campaign execution.
              The campaign targets all active users with at least one active employment.
            </p>
            {hasEmployers &&
              <p>
                {`Filtered by ${employers.length} employer${employers.length > 1 ? 's' : ''}.`}
              </p>
            }
            {renderSelectedUsers()}
          </>
        );
      }

      case 'activeUsers':
        return (
          <>
            <p>
              <span>All active users.</span> Recipients are determined during campaign execution. The campaign targets
              all users that are either active or locked.
            </p>
            {renderSelectedUsers()}
          </>
        );

      default:
        return (
          <p>
            <span>Unknown audience.</span> The audience type is not recognized. Something may be wrong with the campaign.
          </p>
        );
    }
  };

  return (
    <>
      <DetailsCard onClick={onClick}>
        <h6><FontAwesomeIcon icon={faUsers} className={styles.usersIcon} style={{ marginRight: 6 }} /> Audience</h6>
        {renderAudienceInfo()}
      </DetailsCard>

      {audience && (
        <CampaignRecipientsModal
          recipients={audience.recipients}
          notice={"This list shows the currently resolved recipients. Please note that for dynamic audience types," +
            "the actual recipients are determined at campaign execution time and may differ from what is shown here."}
          visible={showRecipientsModal}
          onCloseClick={() => setShowRecipientsModal(false)}
        />
      )}
    </>
  );
};

const useStyles = createUseStyles({
  usersSection: {
    marginTop: '8px',
  },
  usersCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  usersIcon: {
    color: Colors.darkGray2,
    fontSize: '14px',
  },
  countText: {
    fontSize: '14px',
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    backgroundColor: Colors.darkGrayCard,
    border: `1px solid ${Colors.darkGrayBorder}`,
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
      borderColor: Colors.white,
    },
  },
  countNumber: {
    color: Colors.white,
    fontWeight: 'bold',
    fontSize: '13px',
  },
  viewIcon: {
    color: Colors.darkGray1,
    fontSize: '12px',
    transition: 'color 0.2s ease',
    '$countBadge:hover &': {
      color: Colors.white,
    },
  },
});

export default AudienceCard;
